/**
 * ComputerScreen — Desktop-style overlay reusing the same 4 apps as PhoneScreen.
 * Apps live in PhoneApps.js. The computer renders them as a windowed desktop.
 *
 * Opens when the player presses U with a COMPUTER item in inventory (or in creative).
 */

import { getPhoneApps } from './PhoneApps.js';

export class ComputerScreen {
  constructor({ world, player } = {}) {
    this.world = world;
    this.player = player;
    this.isOpen = false;

    this._buildOverlay();
    document.body.appendChild(this.overlay);
  }

  _buildOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'computer-overlay';
    this.overlay.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.85);
      z-index: 950;
      display: none;
      justify-content: center;
      align-items: center;
      cursor: default;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;

    // Monitor body
    this.monitor = document.createElement('div');
    this.monitor.style.cssText = `
      width: min(90vw, 1100px);
      height: min(80vh, 700px);
      background: #2A2A2C;
      border-radius: 18px;
      padding: 14px;
      box-shadow: 0 30px 100px rgba(0,0,0,0.7), inset 0 0 0 2px #3A3A3C;
      display: flex; flex-direction: column;
      position: relative;
    `;
    this.overlay.appendChild(this.monitor);

    // Screen
    this.screen = document.createElement('div');
    this.screen.style.cssText = `
      flex: 1; border-radius: 10px;
      background: linear-gradient(160deg, #6BA8E2 0%, #2E6498 100%);
      overflow: hidden;
      position: relative;
    `;
    this.monitor.appendChild(this.screen);

    // Menu bar (Mac-style)
    const menuBar = document.createElement('div');
    menuBar.style.cssText = `
      position: absolute; top: 0; left: 0; right: 0;
      height: 28px;
      background: rgba(255,255,255,0.4);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      display: flex; align-items: center;
      padding: 0 12px;
      color: white; font-size: 13px; font-weight: 500;
      z-index: 5;
      gap: 18px;
    `;
    menuBar.innerHTML = `
      <span style="font-weight:700;">mundofede</span>
      <span style="opacity:0.85;">Archivo</span>
      <span style="opacity:0.85;">Editar</span>
      <span style="opacity:0.85;">Ver</span>
      <span style="margin-left:auto; opacity:0.85;">9:41 AM</span>
    `;
    this.screen.appendChild(menuBar);

    // Window container (where apps open)
    this.windowArea = document.createElement('div');
    this.windowArea.style.cssText = `
      position: absolute;
      top: 40px; left: 24px; right: 24px; bottom: 80px;
      display: none;
      flex-direction: column;
      background: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 20px 60px rgba(0,0,0,0.4);
    `;
    this.screen.appendChild(this.windowArea);

    // Window header (close button)
    this.windowHeader = document.createElement('div');
    this.windowHeader.style.cssText = `
      height: 32px;
      background: #E8E8ED;
      display: flex; align-items: center;
      padding: 0 12px; gap: 8px;
      border-bottom: 1px solid #D0D0D6;
    `;
    this.windowHeader.innerHTML = `
      <div class="win-close" style="width:12px;height:12px;border-radius:50%;background:#FF5F57;cursor:pointer;"></div>
      <div style="width:12px;height:12px;border-radius:50%;background:#FEBC2E;"></div>
      <div style="width:12px;height:12px;border-radius:50%;background:#28C840;"></div>
      <div class="win-title" style="margin-left:12px;font-size:13px;color:#333;font-weight:600;"></div>
    `;
    this.windowArea.appendChild(this.windowHeader);
    this.windowHeader.querySelector('.win-close').onclick = () => this._closeWindow();

    // Window body
    this.windowBody = document.createElement('div');
    this.windowBody.style.cssText = `
      flex: 1; overflow-y: auto; background: white;
    `;
    this.windowArea.appendChild(this.windowBody);

    // Dock (bottom app launcher)
    this.dock = document.createElement('div');
    this.dock.style.cssText = `
      position: absolute;
      bottom: 16px; left: 50%;
      transform: translateX(-50%);
      background: rgba(255,255,255,0.35);
      backdrop-filter: blur(30px);
      -webkit-backdrop-filter: blur(30px);
      border-radius: 18px;
      padding: 8px 12px;
      display: flex; gap: 12px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.3);
    `;
    this.screen.appendChild(this.dock);

    const apps = getPhoneApps();
    for (const app of apps) {
      const icon = document.createElement('div');
      icon.title = app.name;
      icon.style.cssText = `
        width: 52px; height: 52px;
        background: ${app.iconBg};
        border-radius: 12px;
        display: flex; align-items: center; justify-content: center;
        color: white; font-size: 28px; font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 8px rgba(0,0,0,0.3);
        transition: transform 0.15s ease-out;
      `;
      icon.textContent = app.iconText;
      icon.onmouseover = () => { icon.style.transform = 'translateY(-6px) scale(1.1)'; };
      icon.onmouseout = () => { icon.style.transform = ''; };
      icon.onclick = () => this._openApp(app);
      this.dock.appendChild(icon);
    }

    // Close button (top-right corner of the monitor frame)
    this.closeBtn = document.createElement('button');
    this.closeBtn.textContent = '× Cerrar (Esc)';
    this.closeBtn.style.cssText = `
      position: absolute;
      top: -38px; right: 0;
      padding: 6px 14px;
      background: rgba(255,255,255,0.15);
      color: white;
      border: 1px solid rgba(255,255,255,0.3);
      border-radius: 8px;
      cursor: pointer;
      font-size: 13px;
    `;
    this.closeBtn.onclick = () => this.close();
    this.monitor.appendChild(this.closeBtn);
  }

  _openApp(app) {
    this.windowHeader.querySelector('.win-title').textContent = app.name;
    this.windowBody.innerHTML = app.render({ world: this.world, player: this.player });
    this.windowArea.style.display = 'flex';
    if (app.onMount) app.onMount(this.windowBody, { world: this.world, player: this.player });
  }

  _closeWindow() {
    this.windowArea.style.display = 'none';
    this.windowBody.innerHTML = '';
  }

  open() {
    this.isOpen = true;
    this.overlay.style.display = 'flex';
    this._closeWindow();
  }

  close() {
    this.isOpen = false;
    this.overlay.style.display = 'none';
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }
}
